import React, { useState, useEffect, useRef } from 'react';
import { 
  XMarkIcon, 
  PaperClipIcon, 
  UserGroupIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { Email, EmailFormData, SmtpSettings, Lead } from '../types/Email';
import { emailApi } from '../services/emailApi';

interface ComposeEmailModalProps {
  onClose: () => void;
  onEmailSent: () => void;
  smtpSettings: SmtpSettings | null;
  draftEmail?: Email | null;
  preFilledToEmail?: string;
}

const ComposeEmailModal: React.FC<ComposeEmailModalProps> = ({
  onClose,
  onEmailSent,
  smtpSettings,
  draftEmail,
  preFilledToEmail
}) => {
  const [formData, setFormData] = useState<EmailFormData>({
    subject: '',
    body: '',
    htmlBody: '',
    toEmails: [],
    ccEmails: [],
    bccEmails: [],
    attachments: []
  });

  const [leads, setLeads] = useState<Lead[]>([]);
  const [showLeadSelector, setShowLeadSelector] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');
  const [isHtmlMode, setIsHtmlMode] = useState(false);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  // Load draft email data when editing or pre-filled email
  useEffect(() => {
    if (draftEmail) {
      setFormData({
        subject: draftEmail.subject || '',
        body: draftEmail.body || '',
        htmlBody: draftEmail.htmlBody || '',
        toEmails: draftEmail.toEmails || [],
        ccEmails: draftEmail.ccEmails || [],
        bccEmails: draftEmail.bccEmails || [],
        attachments: []
      });
      setEmailInput(draftEmail.toEmails?.join(', ') || '');
      setCcInput(draftEmail.ccEmails?.join(', ') || '');
      setBccInput(draftEmail.bccEmails?.join(', ') || '');
    } else if (preFilledToEmail) {
      setFormData(prev => ({
        ...prev,
        toEmails: [preFilledToEmail]
      }));
      setEmailInput(preFilledToEmail);
    }
  }, [draftEmail, preFilledToEmail]);

  const fetchLeads = async () => {
    try {
      const leadsData = await emailApi.getLeadsForEmail();
      setLeads(leadsData);
    } catch (error) {
      console.error('Error fetching leads:', error);
    }
  };

  const handleInputChange = (field: keyof EmailFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleEmailInputChange = (value: string, type: 'to' | 'cc' | 'bcc') => {
    const emails = emailApi.parseEmailList(value);
    const validEmails = emails.filter(email => emailApi.validateEmail(email));
    
    switch (type) {
      case 'to':
        setEmailInput(value);
        handleInputChange('toEmails', validEmails);
        break;
      case 'cc':
        setCcInput(value);
        handleInputChange('ccEmails', validEmails);
        break;
      case 'bcc':
        setBccInput(value);
        handleInputChange('bccEmails', validEmails);
        break;
    }
  };

  const handleLeadSelect = (lead: Lead) => {
    if (!formData.toEmails.includes(lead.email)) {
      const newEmails = [...formData.toEmails, lead.email];
      setFormData(prev => ({ ...prev, toEmails: newEmails }));
      setEmailInput(emailApi.formatEmailList(newEmails));
    }
    setShowLeadSelector(false);
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...files]
    }));
  };

  const removeAttachment = (index: number) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (saveAsDraft: boolean = false) => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.subject.trim()) {
        setError('Subject is required');
        return;
      }
      if (!formData.body.trim()) {
        setError('Email body is required');
        return;
      }
      if (formData.toEmails.length === 0) {
        setError('At least one recipient is required');
        return;
      }

      await emailApi.sendEmail(formData, saveAsDraft);
      onEmailSent();
    } catch (error) {
      console.error('Error sending email:', error);
      setError('Failed to send email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const toggleHtmlMode = () => {
    if (isHtmlMode) {
      // Convert HTML to plain text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formData.htmlBody || formData.body;
      setFormData(prev => ({ ...prev, body: tempDiv.textContent || tempDiv.innerText || '' }));
    } else {
      // Convert plain text to HTML
      const htmlBody = formData.body.replace(/\n/g, '<br>');
      setFormData(prev => ({ ...prev, htmlBody }));
    }
    setIsHtmlMode(!isHtmlMode);
  };

  const generateEmailPreview = () => {
    const appLogoUrl = 'https://via.placeholder.com/150x50/3B82F6/FFFFFF?text=LedsHive';
    const organizationName = 'Your Organization'; // This could be fetched from context or props
    const fromName = smtpSettings?.fromName || 'Sender';
    
    // Convert line breaks to HTML
    const formattedBody = (formData.htmlBody || formData.body || '').replace(/\n/g, '<br>');
    
    return `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${formData.subject || 'Email Preview'}</title>
    <style>
        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            line-height: 1.6;
            color: #333333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f8f9fa;
        }
        .email-container {
            background-color: #ffffff;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
            overflow: hidden;
        }
        .header {
            background: linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%);
            padding: 30px 20px;
            text-align: center;
            color: white;
        }
        .org-logo {
            max-height: 60px;
            max-width: 200px;
            margin-bottom: 15px;
        }
        .app-logo {
            max-height: 30px;
            max-width: 120px;
            margin-top: 15px;
            opacity: 0.8;
        }
        .org-name {
            font-size: 24px;
            font-weight: 600;
            margin: 0;
        }
        .content {
            padding: 30px 20px;
        }
        .message-body {
            font-size: 16px;
            line-height: 1.7;
            margin-bottom: 30px;
        }
        .footer {
            background-color: #f8f9fa;
            padding: 20px;
            text-align: center;
            border-top: 1px solid #e9ecef;
        }
        .footer-text {
            font-size: 14px;
            color: #6c757d;
            margin: 0;
        }
        .powered-by {
            font-size: 12px;
            color: #9ca3af;
            margin-top: 10px;
        }
        .powered-by a {
            color: #3B82F6;
            text-decoration: none;
        }
        .powered-by a:hover {
            text-decoration: underline;
        }
    </style>
</head>
<body>
    <div class="email-container">
        <div class="header">
            <h1 class="org-name">${organizationName}</h1>
        </div>
        
        <div class="content">
            <div class="message-body">
                ${formattedBody || '<em>No content yet</em>'}
            </div>
        </div>
        
        <div class="footer">
            <p class="footer-text">
                This email was sent by ${fromName} from ${organizationName}
            </p>
            <div class="powered-by">
                Powered by <a href="#" target="_blank">LedsHive</a>
                <img src="${appLogoUrl}" alt="LedsHive" class="app-logo" style="display: block; margin: 10px auto 0;">
            </div>
        </div>
    </div>
</body>
</html>`;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-4 mx-auto p-0 border w-11/12 md:w-4/5 lg:w-3/4 xl:w-2/3 shadow-lg rounded-md bg-white max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            {formData.subject || 'Compose Email'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-md p-4">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Recipients */}
          <div className="space-y-4">
            {/* To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                To *
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={emailInput}
                  onChange={(e) => handleEmailInputChange(e.target.value, 'to')}
                  placeholder="Enter email addresses separated by commas"
                  className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
                <button
                  onClick={() => setShowLeadSelector(true)}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <UserGroupIcon className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* CC */}
            {showCc && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CC
                </label>
                <input
                  type="text"
                  value={ccInput}
                  onChange={(e) => handleEmailInputChange(e.target.value, 'cc')}
                  placeholder="Enter CC email addresses"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            )}

            {/* BCC */}
            {showBcc && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  BCC
                </label>
                <input
                  type="text"
                  value={bccInput}
                  onChange={(e) => handleEmailInputChange(e.target.value, 'bcc')}
                  placeholder="Enter BCC email addresses"
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                />
              </div>
            )}

            {/* CC/BCC Toggle Buttons */}
            <div className="flex space-x-2">
              {!showCc && (
                <button
                  onClick={() => setShowCc(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + CC
                </button>
              )}
              {!showBcc && (
                <button
                  onClick={() => setShowBcc(true)}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  + BCC
                </button>
              )}
            </div>
          </div>

          {/* Subject */}
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subject *
            </label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => handleInputChange('subject', e.target.value)}
              placeholder="Enter email subject"
              className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          {/* Attachments */}
          {formData.attachments.length > 0 && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Attachments
              </label>
              <div className="space-y-2">
                {formData.attachments.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded-md">
                    <div className="flex items-center space-x-2">
                      <PaperClipIcon className="h-4 w-4 text-gray-400" />
                      <span className="text-sm text-gray-900">{file.name}</span>
                      <span className="text-xs text-gray-500">
                        ({emailApi.formatFileSize(file.size)})
                      </span>
                    </div>
                    <button
                      onClick={() => removeAttachment(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Message */}
          <div className="mt-4">
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700">
                Message *
              </label>
              <div className="flex space-x-2">
                <button
                  onClick={toggleHtmlMode}
                  className={`text-xs px-2 py-1 rounded ${
                    isHtmlMode 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {isHtmlMode ? 'HTML' : 'Text'}
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-xs px-2 py-1 bg-gray-100 text-gray-800 rounded hover:bg-gray-200"
                >
                  <PaperClipIcon className="h-3 w-3 inline mr-1" />
                  Attach
                </button>
              </div>
            </div>
            
            {isHtmlMode ? (
              <textarea
                value={formData.htmlBody || ''}
                onChange={(e) => handleInputChange('htmlBody', e.target.value)}
                placeholder="Enter HTML content"
                rows={12}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm font-mono"
              />
            ) : (
              <textarea
                value={formData.body}
                onChange={(e) => handleInputChange('body', e.target.value)}
                placeholder="Enter your message"
                rows={12}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            )}
          </div>
        </div>

        {/* Email Preview */}
        {showPreview && (
          <div className="border-t border-gray-200 bg-gray-50 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">Email Preview</h3>
            <div className="bg-white border border-gray-200 rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="email-preview" dangerouslySetInnerHTML={{ __html: generateEmailPreview() }} />
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-between items-center p-6 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            From: {smtpSettings?.fromName} &lt;{smtpSettings?.fromEmail}&gt;
          </div>
          <div className="flex space-x-3">
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {showPreview ? 'Hide Preview' : 'Preview'}
            </button>
            <button
              onClick={() => handleSubmit(true)}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={() => handleSubmit(false)}
              disabled={loading || !smtpSettings}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.zip,.rar"
        />
      </div>

      {/* Lead Selector Modal */}
      {showLeadSelector && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-60">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Select Leads</h3>
              <button
                onClick={() => setShowLeadSelector(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {leads.map((lead) => (
                <div
                  key={lead.id}
                  onClick={() => handleLeadSelect(lead)}
                  className="flex items-center justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {lead.firstName} {lead.lastName}
                    </div>
                    <div className="text-sm text-gray-500">{lead.email}</div>
                    {lead.company && (
                      <div className="text-xs text-gray-400">{lead.company}</div>
                    )}
                  </div>
                  <PlusIcon className="h-4 w-4 text-gray-400" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComposeEmailModal;
